import { createEntityAdapter, createSlice, isAnyOf } from '@reduxjs/toolkit';

import i18n from 'i18next';
import { enqueueSnackbar, closeSnackbar } from 'notistack';
import { setFilePermissionViewCount } from 'scenes/ProjectGallery/store/actions/projectGallery.actions';
import { loadProjectFolders } from 'scenes/ProjectBank/store/thunks/loadProjectFolders.thunk';
import {
  EMPTY_BANK_FILTERS,
  MEDIA_TAG_MATCH,
} from 'scenes/ProjectBank/utils/bankItemFilter';
import { isSpecificFloorplanId } from 'scenes/ProjectBank/utils/bankFloorplans';
import { GALLERY_VIEW_SORT_BY } from 'scenes/ProjectBank/utils/bankItemSort';
import { BANK_LOCATION_KINDS } from 'scenes/ProjectBank/utils/bankLocation';
import { getTilaajavastuuFolderId } from 'scenes/ProjectBank/utils/bankSidebarCategories';
import {
  setFolderTree,
  setInitialProjectBankPath,
  setSelectedProjectBankFolder,
} from 'store/filepermissions/actions/filepermissions.actions';
// The thunks' own files, not the slices' indexes: those pull in notistack.
import { fetchSensorFloorplanPlacements } from 'store/floorplanPlacements/thunks/floorplanPlacements.thunks';
import { loadProjectFloorplans } from 'store/floorplans/thunks/loadProjectFloorplans.thunk';
import {
  applyBankLocation,
  clearBankFilters,
  clearSelectedPrivateProjectBankItems,
  clearSelectedProjectBankFiles,
  closePdfPreviewDialog,
  closePrivateProjectBankTreeFolders,
  closeProjectBank,
  closeProjectTreeFolders,
  closeTransferFolderStructureDialog,
  openPdfPreviewDialog,
  openPrivateProjectBankTreeFolders,
  openProjectTilaajavastuuFolder,
  openProjectTreeFolders,
  openTransferFolderStructureDialog,
  restoreProjectBankState,
  selectPrivateProjectBankItem,
  setBankSearchText,
  setIsAddFolderDialogOpen,
  setIsProjectBankFileSelectDialogOpen,
  setProjectBankGalleryView,
  setProjectBankViewMode,
  setSelectedPrivateProjectBankFolderId,
  setSelectedPrivateProjectBankItems,
  setSelectedProjectBankDataTransferTargetVisibilityMode,
  setSelectedProjectBankVisibilityMode,
  setSelectedTilaajavastuuBusinessId,
  setSortBy,
  setUpdateItemsInState,
  toggleSelectProjectBankFile,
  updateBankFilters,
  markProjectBankItemDeleted,
  removeMovedProjectBankFile,
  revealProjectBankTreeFolder,
  clearRevealedProjectBankTreeFolder,
  projectBankGalleryMediaPageLoaded,
  requestBankFloorplansReload,
  requestProjectBankGalleryMediaReload,
  setBankMapView,
  setIsBankAddFloorplanDialogOpen,
  modelPreviewStored,
} from '../actions/project-bank.actions';
import { loadBankFloorplans } from '../thunks/loadBankFloorplans.thunk';
import { searchBankPhotosWithAi } from '../thunks/searchBankPhotosWithAi.thunk';
import { createProjectBankFile } from '../thunks/createProjectBankFile.thunk';
import { createProjectBankFolder } from '../thunks/createProjectBankFolder.thunk';
import { deleteProjectBankForm } from '../thunks/deleteProjectBankForm.thunk';
import { deleteTilaajavastuuDocument } from '../thunks/deleteTilaajavastuuDocument.thunk';
import { applyProjectBankUrlLocation } from '../thunks/applyProjectBankUrlLocation.thunk';
import { duplicateItems } from '../thunks/duplicateItems.thunk';
import { initProjectBank } from '../thunks/initProjectBank.thunk';
import { loadBusinessTilaajavastuuIntegrationStatus } from '../thunks/loadBusinessTilaajavastuuIntegrationStatus.thunk';
import { loadProjectAndSubProjectTilaajavastuuDocuments } from '../thunks/loadProjectAndSubProjectTilaajavastuuDocuments.thunk copy';
import { loadProjectBankFileSizes } from '../thunks/loadProjectBankFileSizes.thunk';
import { loadProjectBankFolderContents } from '../thunks/loadProjectBankFolderContents.thunk';
import { loadProjectBankGalleryMedia } from '../thunks/loadProjectBankGalleryMedia.thunk';
import { loadProjectBankInteractions } from '../thunks/loadProjectBankInteractions.thunk';
import { loadProjectBankStorageUsage } from '../thunks/loadProjectBankStorageUsage.thunk';
import { loadProjectBankFormsFolderContents } from '../thunks/loadProjectBankFormsFolderContents.thunk';
import { loadProjectTilaajavastuuDocuments } from '../thunks/loadProjectTilaajavastuuDocuments.thunk';
import { createPrivateProjectBankFolder } from '../thunks/private-project-bank/createPrivateProjectBankFolder.thunk';
import { deletePrivateProjectBankItem } from '../thunks/private-project-bank/deletePrivateProjectBankFile.thunk';
import { loadPrivateProjectBankItemsOfProject } from '../thunks/private-project-bank/loadPrivateProjectBankItemsOfProject.thunk';
import { renamePrivateProjectBankItem } from '../thunks/private-project-bank/renamePrivateProjectBankItem.thunk';
import { uploadPrivateProjectBankFile } from '../thunks/private-project-bank/uploadPrivateProjectBankFile.thunk';
import { setAsDeletedProjectBankFilePermission } from '../thunks/setAsDeletedProjectBankFilePermission.thunk';
import { transferFolderStructureToProject } from '../thunks/transferFolderStructureToProject.thunk';
import { transferItems } from '../thunks/transferItems.thunk';
import { transferItemsBetweenPrivateAndPublicProjectBank } from '../thunks/transferItemsBetweenPrivateAndPublicProjectBank.thunk';
import { updateProjectBankItem } from '../thunks/updateProjectBankItem.thunk';

const filesFilePermissionsAdapter = createEntityAdapter();
const projectFolderFilePermissionsAdapter = createEntityAdapter();
const projectBankFormsFoldersAdapter = createEntityAdapter();
const projectBankFormsFilesAdapter = createEntityAdapter();
const tilaajavastuuDocumentsAdapter = createEntityAdapter({
  selectId: (tilaajavastuuDocument) =>
    `${tilaajavastuuDocument.projectId}#${tilaajavastuuDocument.businessId}`,
});

const privateProjectBankItemsAdapter = createEntityAdapter();

const initialState = {
  // True once initProjectBank has loaded the folders and the private items of
  // the route project and rooted the tree; the scene's URL sync waits for it
  // before opening what the URL names. Off again while a new init runs and
  // when the bank closes. `initRequestId` is the init that owns the slice:
  // an older one still running (a quick hop between banks) finds its id gone
  // and stops, and the scene's URL sync answers only to its own init.
  isInitialised: false,
  initRequestId: '',
  // The latest URL apply (applyProjectBankUrlLocation); an older one that
  // waited for a load finds its id gone and stops.
  latestUrlApplyRequestId: '',
  projectTreeLoading: false,
  isFolderFilesLoading: false,
  filesFilePermissions: filesFilePermissionsAdapter.getInitialState(),
  projectFolderFilePermissions:
    projectFolderFilePermissionsAdapter.getInitialState(),
  tilaajavastuuDocuments: tilaajavastuuDocumentsAdapter.getInitialState(),
  tilaajavastuuIntegrationStatusesByBusinessId: {},
  selectedTilaajavastuuBusinessId: '',
  selectedFolderPath: '',
  selectedFolderId: '',
  initialProjectBankPath: '',
  folderTree: {},
  sortBy: '',
  // Open as the project's gallery (the Galleria button, `?view=gallery`):
  // its images folder alone, no sidebar, newest first.
  isGalleryView: false,
  // The gallery view's list of the project's photos and videos from every
  // folder (loadProjectBankGalleryMedia): whose it is, how far along, and
  // which request's pages count. The rows themselves go into
  // filesFilePermissions.
  galleryMedia: { projectId: '', status: 'idle', requestId: '' },
  // The gallery view's floorplans (loadBankFloorplans): whose, how the load
  // went ('stale' asks for it again), and which request counts. The data is
  // the shared floorplans and floorplanPlacements slices'.
  bankFloorplansLoad: { projectId: '', status: 'idle', requestId: '' },
  // The gallery view shows its floorplan map (Kartta) instead of the photos
  // (mirrored from its view mode, hooks/useBankMapView).
  isMapView: false,
  isAddFloorplanDialogOpen: false,
  isLoadingTilaajavastuuDocuments: false,
  selectedTilaajavastuuFolderProjectId: '',
  openedProjectTreeFolderIds: [],
  // Folder whose tree row waits to be scrolled into view (see
  // revealProjectBankTreeFolder); the tree pane clears it.
  revealedTreeFolderId: null,
  isPdfPreviewDialogOpen: false,
  isProjectBankFileSelectDialogOpen: false,
  isTransferFolderStructureDialogOpen: false,
  isLoadingFolderStructureTransfer: false,
  transferFolderStructureTargetProjectId: '',
  selectedPreviewTilaajavastuuDocument: {},
  // What a phone has in front: 'tree' while the sidebar drawer is open,
  // 'folder' while it is closed over the files. The bank opens with it
  // closed, and every navigation to a folder closes it.
  projectBankViewMode: 'folder',
  selectedProjectBankFiles: [],
  updateLoading: false,
  updateError: null,
  isDeleting: false,
  selectedProjectBankVisibilityMode: 'PUBLIC',
  selectedProjectBankDataTransferTargetVisibilityMode: 'PUBLIC',
  isAddFolderDialogOpen: false,
  isAddingFolder: false,
  projectBankFormsFolders: projectBankFormsFoldersAdapter.getInitialState(),
  projectBankFormsFiles: projectBankFormsFilesAdapter.getInitialState(),
  loadedProjectBankFormsByProjectId: {},
  // BANK VIEW: filters, search, file sizes
  bankFilters: { ...EMPTY_BANK_FILTERS },
  bankSearchText: '',
  // The search box's AI search (searchBankPhotosWithAi): where it stands and
  // the words it was asked. The answer itself is the Nerobotti slice's
  // photo filter; this only keeps one search in flight at a time and lets
  // the box show it.
  aiSearch: { status: 'idle', query: '' },
  fileSizesById: {},
  pendingFileSizeIds: {},
  // Project ids whose interactions (reactions) this bank session has loaded
  // or is loading; loadProjectBankFolderContents skips them.
  interactionsLoadedProjectIds: {},
  // The business's bank storage usage (sidebar "Tallennustila"): the latest
  // answer of loadProjectBankStorageUsage and its status.
  storageUsage: null,
  storageUsageStatus: 'idle',
  // PRIVATE PROJECT BANK
  selectedPrivateProjectBankFolderId: '',
  privateProjectBankItems: privateProjectBankItemsAdapter.getInitialState(),
  openedPrivateProjectBankTreeFolderIds: [],
  selectedPrivateProjectBankItemIds: [],
};

/**
 * The gallery view's list is to be loaded again (its hook asks once it sees
 * `idle`): after a change it cannot follow row by row. Nothing outside the
 * gallery view, or before a list was asked for.
 */
const markGalleryMediaForReload = (state) => {
  if (state.isGalleryView && state.galleryMedia.projectId) {
    state.galleryMedia.status = 'idle';
  }
};

export const projectBankSlice = createSlice({
  name: 'projectBank',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(loadProjectFolders.pending, (state, action) => {
        state.projectTreeLoading = true;
      })
      .addCase(loadProjectFolders.fulfilled, (state, action) => {
        projectFolderFilePermissionsAdapter.setMany(
          state.projectFolderFilePermissions,
          action.payload
        );
        state.projectTreeLoading = false;
      })
      .addCase(loadProjectFolders.rejected, (state, action) => {
        // A load the project switched away from mid-flight says so; the
        // new project's load owns the spinner.
        if (action.payload?.stale) return;
        state.projectTreeLoading = false;
      })
      .addCase(initProjectBank.pending, (state, action) => {
        state.isInitialised = false;
        state.initRequestId = action.meta.requestId;
      })
      .addCase(initProjectBank.fulfilled, (state, action) => {
        if (action.payload?.stale) return;
        if (action.meta.requestId !== state.initRequestId) return;
        state.isInitialised = true;
      })
      .addCase(applyProjectBankUrlLocation.pending, (state, action) => {
        state.latestUrlApplyRequestId = action.meta.requestId;
      })
      .addCase(applyBankLocation, (state, action) => {
        const {
          location,
          treeFolderIdsToOpen = [],
          privateTreeFolderIdsToOpen = [],
          publicFolder = null,
        } = action.payload || {};
        if (!location) return;

        // The mode switch resets the filters and the search, as the tab does.
        const targetMode =
          location.kind === BANK_LOCATION_KINDS.PRIVATE ? 'PRIVATE' : 'PUBLIC';
        if (state.selectedProjectBankVisibilityMode !== targetMode) {
          state.selectedProjectBankVisibilityMode = targetMode;
          state.bankFilters = { ...EMPTY_BANK_FILTERS };
          state.bankSearchText = '';
        }
        // The location shows in the files: on a phone the drawer closes.
        state.projectBankViewMode = 'folder';

        if (location.kind === BANK_LOCATION_KINDS.PRIVATE) {
          const folderId = location.folderId || '';
          if (state.selectedPrivateProjectBankFolderId !== folderId) {
            state.selectedPrivateProjectBankFolderId = folderId;
            state.selectedPrivateProjectBankItemIds = [];
            state.bankSearchText = '';
          }
          privateTreeFolderIdsToOpen.forEach((id) => {
            if (!state.openedPrivateProjectBankTreeFolderIds.includes(id)) {
              state.openedPrivateProjectBankTreeFolderIds.push(id);
            }
          });
          // Landing in the private bank: the public bank underneath gets a
          // folder too (the root), so its tab does not open on nothing.
          if (publicFolder && !state.selectedFolderId) {
            state.selectedFolderId = publicFolder.folderId;
            state.selectedFolderPath = publicFolder.path;
          }
          return;
        }

        const isTilaajavastuu =
          location.kind === BANK_LOCATION_KINDS.TILAAJAVASTUU;
        const folderId = isTilaajavastuu
          ? getTilaajavastuuFolderId(location.projectId)
          : location.folderId;
        const path = isTilaajavastuu ? undefined : location.path;
        if (
          state.selectedFolderId !== folderId ||
          state.selectedFolderPath !== path
        ) {
          state.selectedFolderId = folderId;
          state.selectedFolderPath = path;
          state.bankSearchText = '';
        }
        state.selectedTilaajavastuuFolderProjectId = isTilaajavastuu
          ? location.projectId
          : '';
        state.selectedTilaajavastuuBusinessId = isTilaajavastuu
          ? location.businessId || ''
          : '';
        treeFolderIdsToOpen.forEach((id) => {
          if (!state.openedProjectTreeFolderIds.includes(id)) {
            state.openedProjectTreeFolderIds.push(id);
          }
        });
      })

      .addCase(loadProjectBankFolderContents.pending, (state, action) => {
        state.isFolderFilesLoading = true;
      })
      .addCase(loadProjectBankFolderContents.fulfilled, (state, action) => {
        filesFilePermissionsAdapter.setMany(
          state.filesFilePermissions,
          action.payload || []
        );
        state.isFolderFilesLoading = false;
      })
      // The gallery view's photos from every folder: each page as it lands,
      // the latest request's only (loadProjectBankGalleryMedia).
      .addCase(loadProjectBankGalleryMedia.pending, (state, action) => {
        state.galleryMedia = {
          projectId: action.meta.arg,
          status: 'loading',
          requestId: action.meta.requestId,
        };
      })
      .addCase(projectBankGalleryMediaPageLoaded, (state, action) => {
        const { requestId, items } = action.payload || {};
        if (!requestId || requestId !== state.galleryMedia.requestId) return;
        filesFilePermissionsAdapter.setMany(
          state.filesFilePermissions,
          items || []
        );
      })
      .addCase(loadProjectBankGalleryMedia.fulfilled, (state, action) => {
        if (
          action.meta.requestId !== state.galleryMedia.requestId ||
          (action.payload && action.payload.stale)
        ) {
          return;
        }
        state.galleryMedia.status = 'loaded';
      })
      .addCase(loadProjectBankGalleryMedia.rejected, (state, action) => {
        if (action.meta.requestId !== state.galleryMedia.requestId) return;
        state.galleryMedia.status = 'failed';
      })
      .addCase(requestProjectBankGalleryMediaReload, (state) => {
        markGalleryMediaForReload(state);
      })
      .addCase(loadProjectBankFileSizes.pending, (state, action) => {
        (action.meta.arg || []).forEach((id) => {
          state.pendingFileSizeIds[id] = true;
        });
      })
      .addCase(loadProjectBankFileSizes.fulfilled, (state, action) => {
        Object.assign(state.fileSizesById, action.payload?.sizesById || {});
        (action.meta.arg || []).forEach((id) => {
          delete state.pendingFileSizeIds[id];
        });
      })
      .addCase(loadProjectBankFileSizes.rejected, (state, action) => {
        (action.meta.arg || []).forEach((id) => {
          if (typeof state.fileSizesById[id] !== 'number') {
            state.fileSizesById[id] = null;
          }
          delete state.pendingFileSizeIds[id];
        });
      })
      .addCase(loadProjectBankFolderContents.rejected, (state, action) => {
        state.isFolderFilesLoading = false;
      })
      .addCase(loadProjectBankInteractions.pending, (state, action) => {
        (action.meta.arg || []).forEach((id) => {
          state.interactionsLoadedProjectIds[id] = true;
        });
      })
      .addCase(loadProjectBankInteractions.fulfilled, (state, action) => {
        ((action.payload && action.payload.failedProjectIds) || []).forEach(
          (id) => {
            delete state.interactionsLoadedProjectIds[id];
          }
        );
      })
      .addCase(loadProjectBankInteractions.rejected, (state, action) => {
        (action.meta.arg || []).forEach((id) => {
          delete state.interactionsLoadedProjectIds[id];
        });
      })
      // The gallery's image-view endpoint returns the file's new total view
      // count; the reused viewer dispatches this from the bank too, so the
      // card / row badge follows without a refetch. A no-op for unknown ids.
      .addCase(setFilePermissionViewCount, (state, action) => {
        const { id, viewCount } = action.payload || {};
        if (!id) return;
        filesFilePermissionsAdapter.updateOne(state.filesFilePermissions, {
          id,
          changes: { viewCount },
        });
      })
      .addCase(loadProjectBankStorageUsage.pending, (state) => {
        state.storageUsageStatus = 'loading';
      })
      .addCase(loadProjectBankStorageUsage.fulfilled, (state, action) => {
        state.storageUsage = action.payload || null;
        state.storageUsageStatus = 'loaded';
      })
      .addCase(loadProjectBankStorageUsage.rejected, (state) => {
        state.storageUsageStatus = 'failed';
      })
      .addCase(loadProjectBankFormsFolderContents.pending, (state) => {
        state.isFolderFilesLoading = true;
      })
      .addCase(
        loadProjectBankFormsFolderContents.fulfilled,
        (state, action) => {
          const { projectId, folders = [], files = [] } = action.payload || {};

          const formFolderIdsToRemove = Object.values(
            state.projectBankFormsFolders.entities
          )
            .filter((item) => {
              const itemProjectId =
                typeof item?.project === 'object'
                  ? item?.project?.id
                  : item?.project;
              return itemProjectId === projectId;
            })
            .map((item) => item.id);

          const formFileIdsToRemove = Object.values(
            state.projectBankFormsFiles.entities
          )
            .filter((item) => {
              const itemProjectId =
                typeof item?.project === 'object'
                  ? item?.project?.id
                  : item?.project;
              return itemProjectId === projectId;
            })
            .map((item) => item.id);

          projectBankFormsFoldersAdapter.removeMany(
            state.projectBankFormsFolders,
            formFolderIdsToRemove
          );
          projectBankFormsFilesAdapter.removeMany(
            state.projectBankFormsFiles,
            formFileIdsToRemove
          );

          projectBankFormsFoldersAdapter.addMany(
            state.projectBankFormsFolders,
            folders
          );
          projectBankFormsFilesAdapter.addMany(
            state.projectBankFormsFiles,
            files
          );
          if (action.payload.complete !== false) {
            state.loadedProjectBankFormsByProjectId[projectId] = true;
          }
          state.isFolderFilesLoading = false;
        }
      )
      .addCase(loadProjectBankFormsFolderContents.rejected, (state) => {
        state.isFolderFilesLoading = false;
      })

      .addCase(setSelectedProjectBankFolder, (state, action) => {
        const { folderId, path } = action.payload;
        state.selectedFolderId = folderId;
        state.selectedFolderPath = path;
        state.projectBankViewMode = 'folder';
        state.bankSearchText = '';
        if (folderId && !folderId.includes('tilaajavastuu')) {
          state.selectedTilaajavastuuBusinessId = '';
          state.selectedTilaajavastuuFolderProjectId = '';
        }
      })
      .addCase(setSortBy, (state, action) => {
        state.sortBy = action.payload;
      })
      // The gallery opens newest first; the user may sort otherwise from
      // there, and the bank starts over from its own default when it closes
      // (closeProjectBank) — or when a gallery's state put back after it had
      // closed (the file picker's snapshot) meets a bank that is no gallery.
      .addCase(setProjectBankGalleryView, (state, action) => {
        const isGalleryView = action.payload === true;
        if (isGalleryView && !state.isGalleryView) {
          state.sortBy = GALLERY_VIEW_SORT_BY;
        } else if (
          !isGalleryView &&
          state.isGalleryView &&
          state.sortBy === GALLERY_VIEW_SORT_BY
        ) {
          state.sortBy = '';
        }
        // The floorplans are the gallery's alone: a bank that is no gallery
        // keeps no floorplan filter, map or dialog of it.
        if (!isGalleryView && state.isGalleryView) {
          state.bankFilters.floorplanId = '';
          state.bankFilters.floorplanArea = null;
          state.isMapView = false;
          state.isAddFloorplanDialogOpen = false;
        }
        state.isGalleryView = isGalleryView;
      })
      .addCase(setBankSearchText, (state, action) => {
        state.bankSearchText = action.payload || '';
      })
      .addCase(searchBankPhotosWithAi.pending, (state, action) => {
        state.aiSearch = { status: 'loading', query: action.meta.arg.query };
      })
      .addCase(searchBankPhotosWithAi.fulfilled, (state, action) => {
        state.aiSearch = {
          status: action.payload.status,
          query: action.payload.query,
        };
      })
      .addCase(searchBankPhotosWithAi.rejected, (state, action) => {
        state.aiSearch = { status: 'failed', query: action.meta.arg.query };
      })
      .addCase(updateBankFilters, (state, action) => {
        const payload = action.payload || {};
        const previousFloorplanId = state.bankFilters.floorplanId;
        state.bankFilters = { ...state.bankFilters, ...payload };
        // With fewer than two tags the mode goes back to 'any': the choice is
        // shown only for two or more (with one they keep the same files), and
        // the AI panel picks tags without showing it, so a leftover 'all'
        // would narrow what is picked next out of sight.
        if ((state.bankFilters.mediaTagIds || []).length < 2) {
          state.bankFilters.mediaTagMatch = MEDIA_TAG_MATCH.ANY;
        }
        // An area is drawn on one floorplan: another floorplan, or none,
        // drops it.
        if (
          (payload.floorplanId !== undefined &&
            payload.floorplanId !== previousFloorplanId &&
            payload.floorplanArea === undefined) ||
          !isSpecificFloorplanId(state.bankFilters.floorplanId)
        ) {
          state.bankFilters.floorplanArea = null;
        }
      })
      .addCase(clearBankFilters, (state) => {
        // On the map the floorplan is what is being looked at, not a filter:
        // it stays, and its area goes with the rest.
        const keptFloorplanId =
          state.isMapView &&
          isSpecificFloorplanId(state.bankFilters.floorplanId)
            ? state.bankFilters.floorplanId
            : '';
        state.bankFilters = {
          ...EMPTY_BANK_FILTERS,
          floorplanId: keptFloorplanId,
        };
        state.bankSearchText = '';
      })
      .addCase(setBankMapView, (state, action) => {
        state.isMapView = action.payload === true;
      })
      .addCase(setIsBankAddFloorplanDialogOpen, (state, action) => {
        state.isAddFloorplanDialogOpen = action.payload === true;
      })
      // The gallery view's floorplans: the latest request's answer counts.
      .addCase(loadBankFloorplans.pending, (state, action) => {
        state.bankFloorplansLoad = {
          projectId: action.meta.arg,
          status: 'loading',
          requestId: action.meta.requestId,
        };
      })
      .addCase(loadBankFloorplans.fulfilled, (state, action) => {
        // Stale meanwhile (below): what it loaded was already wiped.
        if (
          action.meta.requestId === state.bankFloorplansLoad.requestId &&
          state.bankFloorplansLoad.status === 'loading'
        ) {
          state.bankFloorplansLoad.status = 'loaded';
        }
      })
      .addCase(loadBankFloorplans.rejected, (state, action) => {
        if (action.meta.requestId === state.bankFloorplansLoad.requestId) {
          state.bankFloorplansLoad.status = 'failed';
        }
      })
      // The shared floorplans slice holds one project's floorplans, and the
      // sensors' placements replace every photo's: loading either for
      // something else (another project's viewer, Renolog) leaves the
      // gallery's floorplans to be loaded again. A failed load waits for the
      // user's Retry.
      .addCase(loadProjectFloorplans.fulfilled, (state, action) => {
        const load = state.bankFloorplansLoad;
        if (
          load.projectId &&
          load.status !== 'failed' &&
          action.meta.arg !== load.projectId
        ) {
          load.status = 'stale';
        }
      })
      .addCase(fetchSensorFloorplanPlacements.fulfilled, (state) => {
        const load = state.bankFloorplansLoad;
        if (load.projectId && load.status !== 'failed') {
          load.status = 'stale';
        }
      })
      .addCase(requestBankFloorplansReload, (state) => {
        if (state.bankFloorplansLoad.projectId) {
          state.bankFloorplansLoad.status = 'stale';
        }
      })
      .addCase(
        setAsDeletedProjectBankFilePermission.fulfilled,
        (state, action) => {
          if (action.payload.type === 'FOLDER') {
            projectFolderFilePermissionsAdapter.upsertOne(
              state.projectFolderFilePermissions,
              action.payload
            );
          } else {
            filesFilePermissionsAdapter.upsertOne(
              state.filesFilePermissions,
              action.payload
            );
          }
        }
      )
      .addCase(updateProjectBankItem.pending, (state, action) => {
        state.updateLoading = true;
      })
      .addCase(updateProjectBankItem.fulfilled, (state, action) => {
        if (action.payload.type === 'FOLDER') {
          projectFolderFilePermissionsAdapter.upsertOne(
            state.projectFolderFilePermissions,
            action.payload
          );
        } else {
          filesFilePermissionsAdapter.upsertOne(
            state.filesFilePermissions,
            action.payload
          );
        }
        state.updateLoading = false;
      })
      .addCase(updateProjectBankItem.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateError = action.error.message;
      })
      .addCase(createProjectBankFolder.fulfilled, (state, action) => {
        projectFolderFilePermissionsAdapter.setOne(
          state.projectFolderFilePermissions,
          action.payload
        );
      })
      .addCase(createProjectBankFile.fulfilled, (state, action) => {
        filesFilePermissionsAdapter.setOne(
          state.filesFilePermissions,
          action.payload
        );
      })
      .addCase(setInitialProjectBankPath, (state, action) => {
        state.initialProjectBankPath = action.payload;
      })
      .addCase(setFolderTree, (state, action) => {
        state.folderTree = action.payload;
      })
      .addCase(closeProjectBank, () => ({
        ...initialState,
      }))
      .addCase(setSelectedTilaajavastuuBusinessId, (state, action) => {
        state.selectedTilaajavastuuBusinessId = action.payload;
      })
      .addCase(openProjectTilaajavastuuFolder, (state, action) => {
        state.selectedTilaajavastuuFolderProjectId = action.payload;
        state.selectedTilaajavastuuBusinessId = '';
      })
      // Every id of the payload opens (an ancestor chain restored from the
      // URL comes as one dispatch); an id already open stays as it is.
      .addCase(openProjectTreeFolders, (state, action) => {
        (action.payload || []).forEach((openedFolderId) => {
          if (!state.openedProjectTreeFolderIds.includes(openedFolderId)) {
            state.openedProjectTreeFolderIds.push(openedFolderId);
          }
        });
      })
      .addCase(openPrivateProjectBankTreeFolders, (state, action) => {
        (action.payload || []).forEach((openedFolderId) => {
          if (
            !state.openedPrivateProjectBankTreeFolderIds.includes(
              openedFolderId
            )
          ) {
            state.openedPrivateProjectBankTreeFolderIds.push(openedFolderId);
          }
        });
      })
      .addCase(closeProjectTreeFolders, (state, action) => {
        const closedFolderIds = action.payload;

        const currentOpenProjectTreeFolders = state.openedProjectTreeFolderIds;

        closedFolderIds.forEach((folderId) => {
          state.openedProjectTreeFolderIds =
            currentOpenProjectTreeFolders.filter(
              (currentOpenFolderId) => currentOpenFolderId !== folderId
            );
        });
      })
      .addCase(closePrivateProjectBankTreeFolders, (state, action) => {
        const closedFolderIds = action.payload;

        const currentOpenProjectTreeFolders =
          state.openedPrivateProjectBankTreeFolderIds;

        closedFolderIds.forEach((folderId) => {
          state.openedPrivateProjectBankTreeFolderIds =
            currentOpenProjectTreeFolders.filter(
              (currentOpenFolderId) => currentOpenFolderId !== folderId
            );
        });
      })
      .addCase(openPdfPreviewDialog, (state, action) => {
        state.selectedPreviewTilaajavastuuDocument = action.payload;
        state.isPdfPreviewDialogOpen = true;
      })
      .addCase(closePdfPreviewDialog, (state, action) => {
        state.isPdfPreviewDialogOpen = false;
        state.selectedPreviewTilaajavastuuDocument = {};
      })
      .addCase(deleteTilaajavastuuDocument.fulfilled, (state, action) => {
        tilaajavastuuDocumentsAdapter.setOne(
          state.tilaajavastuuDocuments,
          action.payload
        );
        state.selectedTilaajavastuuBusinessId = '';
        state.isPdfPreviewDialogOpen = false;
        enqueueSnackbar('Tilaajavastuudokumentti poistettu', {
          variant: 'success',
        });
      })
      .addCase(setProjectBankViewMode, (state, action) => {
        state.projectBankViewMode = action.payload;
      })
      .addCase(toggleSelectProjectBankFile, (state, action) => {
        const alreadySelectedFile = state.selectedProjectBankFiles.includes(
          action.payload
        );

        if (alreadySelectedFile) {
          state.selectedProjectBankFiles =
            state.selectedProjectBankFiles.filter(
              (fileId) => fileId !== action.payload
            );
        } else {
          state.selectedProjectBankFiles.push(action.payload);
        }
      })
      .addCase(clearSelectedProjectBankFiles, (state, action) => {
        state.selectedProjectBankFiles = [];
      })
      .addCase(setIsProjectBankFileSelectDialogOpen, (state, action) => {
        if (action.payload === false) {
          return { ...initialState };
        } else {
          state.isProjectBankFileSelectDialogOpen = action.payload;
        }
      })
      // The snapshot a dialog took before opening the selector over the
      // project bank (see restoreProjectBankState): the bank as it was,
      // minus the selector's own state. A gallery list that was still
      // loading then was cut off when the selector's close reset the slice,
      // so it is to be asked for again rather than waited on.
      .addCase(restoreProjectBankState, (state, action) => ({
        ...action.payload,
        isProjectBankFileSelectDialogOpen: false,
        selectedProjectBankFiles: [],
        galleryMedia:
          action.payload &&
          action.payload.galleryMedia &&
          action.payload.galleryMedia.status !== 'loading'
            ? action.payload.galleryMedia
            : initialState.galleryMedia,
        // Likewise a floorplan load cut off by the reset: loaded again.
        bankFloorplansLoad:
          action.payload &&
          action.payload.bankFloorplansLoad &&
          action.payload.bankFloorplansLoad.status === 'loading'
            ? { ...action.payload.bankFloorplansLoad, status: 'stale' }
            : (action.payload && action.payload.bankFloorplansLoad) ||
              initialState.bankFloorplansLoad,
      }))
      .addCase(openTransferFolderStructureDialog, (state, action) => {
        const { targetProjectId } = action.payload;

        state.isTransferFolderStructureDialogOpen = true;
        state.transferFolderStructureTargetProjectId = targetProjectId;
      })
      .addCase(closeTransferFolderStructureDialog, (state, action) => {
        state.isTransferFolderStructureDialogOpen = false;
        state.transferFolderStructureTargetProjectId = '';
      })
      .addCase(transferFolderStructureToProject.pending, (state, action) => {
        state.isLoadingFolderStructureTransfer = true;
      })
      .addCase(transferFolderStructureToProject.fulfilled, (state, action) => {
        projectFolderFilePermissionsAdapter.setMany(
          state.projectFolderFilePermissions,
          action.payload
        );
        state.isTransferFolderStructureDialogOpen = false;
        state.isLoadingFolderStructureTransfer = false;
        enqueueSnackbar('Kansiorakenne tuotu projektille', {
          variant: 'success',
        });
      })
      .addCase(transferFolderStructureToProject.rejected, (state, action) => {
        state.isLoadingFolderStructureTransfer = false;
        enqueueSnackbar('Virhe kansiorakenteen tuonnissa', {
          variant: 'error',
        });
      })
      .addCase(transferItems.pending, (state, action) => {
        state.isLoadingTransfer = true;
      })
      .addCase(transferItems.fulfilled, (state, action) => {
        const response = action.payload;
        const currentFolderId = state.selectedFolderId;
        const currentlyInTargetFolder =
          response.targetFolder && response.targetFolder === currentFolderId;
        // The gallery lists the project's photos from every folder: one
        // moved to another folder of the same project stays in it. Its key
        // and parent are stale until the next visit, which nothing there
        // goes by (thumbnails, the viewer and edits go by the id).
        const staysInGallery =
          state.isGalleryView &&
          Boolean(response.targetProject) &&
          response.targetProject === response.fromProject;
        // …and the list is loaded again, which brings the moved rows with
        // their new folder (what the file picker lists them by).
        if (staysInGallery) markGalleryMediaForReload(state);

        for (const item of response.results) {
          if (item.status !== 'SUCCESS') continue;
          if (item.type === 'FOLDER') {
            if (currentlyInTargetFolder) {
              const existing =
                state.projectFolderFilePermissions.entities[item.id];
              projectFolderFilePermissionsAdapter.upsertOne(
                state.projectFolderFilePermissions,
                {
                  ...existing,
                  isBeingTransferred: false,
                  isDeleting: false,
                }
              );
            } else {
              projectFolderFilePermissionsAdapter.removeOne(
                state.projectFolderFilePermissions,
                item.id
              );
            }
          } else {
            if (currentlyInTargetFolder || staysInGallery) {
              const existing = state.filesFilePermissions.entities[item.id];
              filesFilePermissionsAdapter.upsertOne(
                state.filesFilePermissions,
                {
                  ...existing,
                  isBeingTransferred: false,
                  isDeleting: false,
                }
              );
            } else {
              filesFilePermissionsAdapter.removeOne(
                state.filesFilePermissions,
                item.id
              );
            }
          }
        }
        state.isLoadingTransfer = false;
      })
      .addCase(transferItems.rejected, (state, action) => {
        state.isLoadingTransfer = false;
      })
      .addCase(duplicateItems.pending, (state, action) => {
        state.isLoadingDuplicate = true;
      })
      .addCase(duplicateItems.fulfilled, (state, action) => {
        const response = action.payload;
        // console.log('response:', response)
        state.isLoadingDuplicate = false;
        // The copies land in the folders they were made in; the gallery
        // lists every folder's photos, so it asks for them.
        markGalleryMediaForReload(state);
      })
      .addCase(duplicateItems.rejected, (state, action) => {
        state.isLoadingDuplicate = false;
      })
      .addCase(setUpdateItemsInState, (state, action) => {
        if (!action.payload || !Array.isArray(action.payload)) return;
        for (const item of action.payload) {
          if (item.isVirtualProjectBankForm) {
            if (item.type === 'FOLDER') {
              projectBankFormsFoldersAdapter.upsertOne(
                state.projectBankFormsFolders,
                item
              );
            } else {
              projectBankFormsFilesAdapter.upsertOne(
                state.projectBankFormsFiles,
                item
              );
            }
          } else if (item.type === 'FOLDER') {
            projectFolderFilePermissionsAdapter.upsertOne(
              state.projectFolderFilePermissions,
              item
            );
          } else {
            filesFilePermissionsAdapter.upsertOne(
              state.filesFilePermissions,
              item
            );
          }
        }
      })
      .addCase(markProjectBankItemDeleted, (state, action) => {
        const { id, deletedAt } = action.payload || {};
        if (!id || !deletedAt) return;

        // Checked against each adapter rather than chosen by type: the caller
        // knows only the id. `updateOne` never inserts, so a row this client
        // never loaded stays absent instead of arriving as a stray entity.
        if (state.filesFilePermissions.entities[id]) {
          filesFilePermissionsAdapter.updateOne(state.filesFilePermissions, {
            id,
            changes: { deletedAt },
          });
        }
        if (state.projectFolderFilePermissions.entities[id]) {
          projectFolderFilePermissionsAdapter.updateOne(
            state.projectFolderFilePermissions,
            { id, changes: { deletedAt } }
          );
        }
      })
      .addCase(removeMovedProjectBankFile, (state, action) => {
        const id = action.payload;
        if (!id) return;
        // The gallery lists the project's photos from every folder: one the
        // assistant moved stays, and the list is loaded again after it
        // (requestProjectBankGalleryMediaReload).
        if (state.isGalleryView) return;

        // Files only: a moved file is the one row a refetch cannot take out of
        // the folder it left. `removeOne` on an id the adapter lacks is a no-op.
        filesFilePermissionsAdapter.removeOne(state.filesFilePermissions, id);
      })
      .addCase(revealProjectBankTreeFolder, (state, action) => {
        const id = action.payload;
        const folders = state.projectFolderFilePermissions.entities;
        if (!id || !folders[id]) return;

        // A row is only drawn while every folder above it is open, and a
        // closed folder can sit above an open one, so the walk goes all the
        // way up. It stops at a parent this client does not hold, and at one
        // it has already met, so a row that is its own ancestor cannot spin
        // it.
        const seen = new Set([id]);
        let parentId = folders[id].parent;
        while (parentId && folders[parentId] && !seen.has(parentId)) {
          seen.add(parentId);
          if (!state.openedProjectTreeFolderIds.includes(parentId)) {
            state.openedProjectTreeFolderIds.push(parentId);
          }
          parentId = folders[parentId].parent;
        }

        state.revealedTreeFolderId = id;
      })
      .addCase(clearRevealedProjectBankTreeFolder, (state) => {
        state.revealedTreeFolderId = null;
      })
      .addCase(
        loadBusinessTilaajavastuuIntegrationStatus.fulfilled,
        (state, action) => {
          const { businessId, status } = action.payload;

          state.tilaajavastuuIntegrationStatusesByBusinessId[businessId] =
            status;
        }
      )
      .addCase(setSelectedPrivateProjectBankFolderId, (state, action) => {
        state.selectedPrivateProjectBankFolderId = action.payload;
        state.selectedPrivateProjectBankItemIds = [];
        state.bankSearchText = '';
        // A private folder opens in the files as a public one does, so on a
        // phone the drawer closes.
        state.projectBankViewMode = 'folder';
      })
      .addCase(
        loadPrivateProjectBankItemsOfProject.fulfilled,
        (state, action) => {
          privateProjectBankItemsAdapter.setMany(
            state.privateProjectBankItems,
            action.payload
          );
        }
      )
      .addCase(setSelectedProjectBankVisibilityMode, (state, action) => {
        state.selectedProjectBankVisibilityMode = action.payload;
        state.bankFilters = { ...EMPTY_BANK_FILTERS };
        state.bankSearchText = '';
      })

      .addCase(
        setSelectedProjectBankDataTransferTargetVisibilityMode,
        (state, action) => {
          state.selectedProjectBankDataTransferTargetVisibilityMode =
            action.payload;
        }
      )
      .addCase(uploadPrivateProjectBankFile.fulfilled, (state, action) => {
        const { createdFileItems } = action.payload;

        if (createdFileItems && createdFileItems.length > 0) {
          privateProjectBankItemsAdapter.addMany(
            state.privateProjectBankItems,
            createdFileItems
          );
        }
      })
      .addCase(createPrivateProjectBankFolder.pending, (state, action) => {
        state.isAddingFolder = true;
      })
      .addCase(createPrivateProjectBankFolder.fulfilled, (state, action) => {
        const { createdFolder } = action.payload;
        if (createdFolder) {
          privateProjectBankItemsAdapter.addOne(
            state.privateProjectBankItems,
            createdFolder
          );
        }
        state.isAddFolderDialogOpen = false;
        state.isAddingFolder = false;
        enqueueSnackbar(i18n.t('bank.add_folder_success'), {
          variant: 'success',
        });
      })
      .addCase(createPrivateProjectBankFolder.rejected, (state, action) => {
        state.isAddingFolder = false;
        enqueueSnackbar(i18n.t('bank.add_folder_error'), {
          variant: 'error',
        });
      })
      .addCase(setIsAddFolderDialogOpen, (state, action) => {
        state.isAddFolderDialogOpen = action.payload;
      })
      .addCase(deletePrivateProjectBankItem.fulfilled, (state, action) => {
        const { deletedPrivateProjectBankItems } = action.payload;

        privateProjectBankItemsAdapter.upsertMany(
          state.privateProjectBankItems,
          deletedPrivateProjectBankItems
        );
      })
      .addCase(renamePrivateProjectBankItem.pending, (state, action) => {
        state.updateLoading = true;
      })
      .addCase(renamePrivateProjectBankItem.fulfilled, (state, action) => {
        privateProjectBankItemsAdapter.upsertOne(
          state.privateProjectBankItems,
          action.payload
        );
        state.updateLoading = false;
        enqueueSnackbar(i18n.t('common.success'), { variant: 'success' });
      })
      .addCase(modelPreviewStored, (state, action) => {
        const { mode, id, modelThumbnailAt, modelThumbnailVersion } =
          action.payload || {};
        if (!id) return;
        const changes = { modelThumbnailAt, modelThumbnailVersion };
        if (mode === 'PRIVATE') {
          privateProjectBankItemsAdapter.updateOne(
            state.privateProjectBankItems,
            { id, changes }
          );
        } else {
          filesFilePermissionsAdapter.updateOne(state.filesFilePermissions, {
            id,
            changes,
          });
        }
      })
      .addCase(renamePrivateProjectBankItem.rejected, (state, action) => {
        state.updateLoading = false;
        enqueueSnackbar(i18n.t('common.error'), { variant: 'error' });
      })
      .addCase(selectPrivateProjectBankItem, (state, action) => {
        const currentSelectedItemIds = state.selectedPrivateProjectBankItemIds;

        if (currentSelectedItemIds.includes(action.payload)) {
          state.selectedPrivateProjectBankItemIds =
            currentSelectedItemIds.filter(
              (itemId) => itemId !== action.payload
            );
        } else {
          state.selectedPrivateProjectBankItemIds = [
            ...currentSelectedItemIds,
            action.payload,
          ];
        }
      })
      .addCase(setSelectedPrivateProjectBankItems, (state, action) => {
        state.selectedPrivateProjectBankItemIds = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(clearSelectedPrivateProjectBankItems, (state, action) => {
        state.selectedPrivateProjectBankItemIds = [];
      })

      .addCase(
        transferItemsBetweenPrivateAndPublicProjectBank.pending,
        (state, action) => {
          const transferMethod = action.meta.arg.transferMethod;

          const notificationText =
            transferMethod === 'COPY'
              ? i18n.t('bank.duplicating')
              : i18n.t('bank.moving');

          enqueueSnackbar(notificationText, {
            variant: 'info',
            persist: true,
            key: 'transfer-items-loading',
          });
        }
      )
      .addCase(
        transferItemsBetweenPrivateAndPublicProjectBank.fulfilled,
        (state, action) => {
          const {
            deletedPrivateProjectBankItems,
            createdPrivateProjectBankItems,
            deletedPublicProjectBankFilePermissions,
            createdPublicProjectBankFilePermissions,
          } = action.payload;

          if (
            deletedPrivateProjectBankItems &&
            deletedPrivateProjectBankItems.length > 0
          ) {
            const deletedIds = deletedPrivateProjectBankItems.map(
              (item) => item.id
            );
            privateProjectBankItemsAdapter.removeMany(
              state.privateProjectBankItems,
              deletedIds
            );
          }

          if (
            createdPrivateProjectBankItems &&
            createdPrivateProjectBankItems.length > 0
          ) {
            privateProjectBankItemsAdapter.upsertMany(
              state.privateProjectBankItems,
              createdPrivateProjectBankItems
            );
          }

          if (
            deletedPublicProjectBankFilePermissions &&
            deletedPublicProjectBankFilePermissions.length > 0
          ) {
            const deletedIds = deletedPublicProjectBankFilePermissions.map(
              (item) => item.id
            );

            filesFilePermissionsAdapter.removeMany(
              state.filesFilePermissions,
              deletedIds
            );
            projectFolderFilePermissionsAdapter.removeMany(
              state.projectFolderFilePermissions,
              deletedIds
            );
          }
          if (
            createdPublicProjectBankFilePermissions &&
            createdPublicProjectBankFilePermissions.length > 0
          ) {
            const files = createdPublicProjectBankFilePermissions.filter(
              (item) => item.type === 'FILE'
            );

            const folders = createdPublicProjectBankFilePermissions.filter(
              (item) => item.type === 'FOLDER'
            );
            filesFilePermissionsAdapter.upsertMany(
              state.filesFilePermissions,
              files
            );
            projectFolderFilePermissionsAdapter.upsertMany(
              state.projectFolderFilePermissions,
              folders
            );
          }

          closeSnackbar('transfer-items-loading');
          enqueueSnackbar(i18n.t('common.success'), {
            variant: 'success',
          });
        }
      )
      .addCase(
        transferItemsBetweenPrivateAndPublicProjectBank.rejected,
        (state, action) => {
          closeSnackbar('transfer-items-loading');
          enqueueSnackbar(i18n.t('common.error'), {
            variant: 'error',
          });
        }
      )
      .addMatcher(
        isAnyOf(
          loadProjectTilaajavastuuDocuments.pending,
          loadProjectAndSubProjectTilaajavastuuDocuments.pending
        ),
        (state, action) => {
          state.isLoadingTilaajavastuuDocuments = true;
        }
      )
      .addMatcher(
        isAnyOf(
          loadProjectTilaajavastuuDocuments.fulfilled,
          loadProjectAndSubProjectTilaajavastuuDocuments.fulfilled
        ),
        (state, action) => {
          tilaajavastuuDocumentsAdapter.setMany(
            state.tilaajavastuuDocuments,
            action.payload
          );
          state.isLoadingTilaajavastuuDocuments = false;
        }
      )
      .addMatcher(
        isAnyOf(
          loadProjectTilaajavastuuDocuments.rejected,
          loadProjectAndSubProjectTilaajavastuuDocuments.rejected
        ),
        (state, action) => {
          state.isLoadingTilaajavastuuDocuments = false;
        }
      );
  },
});
